﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { createUseStyles } from "react-jss";
import { useIntl } from "~base/Internationalization/IntlContext";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";
import type { SelfServiceViewAction } from "../Store";
import type { SelfServiceViewState } from "../Types";
import Grid from "~base/Components/Layout/Grid";
import TitleEmail from "~base/Components/Primitives/TitleEmail";
import TitlePassword from "~base/Components/Primitives/TitlePassword";
import SelfServicePageNavigationControls from "../ChildComponents/PageNavigationControls";
import { useValidation } from "~base/Validation/ValidationContext";
import FieldError from "../ChildComponents/FieldError";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

export interface SelfServicePageAccountProps {
    isLoading: boolean;
    state: SelfServiceViewState;
    dispatch: React.Dispatch<SelfServiceViewAction>;
    postRegistrationRequest: () => void;
}

const useStyles = createUseStyles((theme: SoPNLConsilioTheme) => ({
    container: {},
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const PageAccount: React.FunctionComponent<SelfServicePageAccountProps> = ({
    isLoading,
    state,
    dispatch,
    postRegistrationRequest,
}) => {
    const intl = useIntl();
    const classes = useStyles();
    const validation = useValidation();

    const isEmailValid = validation.email(state.requestData.email).isValid;

    const isFormValid =
        isEmailValid &&
        state.requestData.password &&
        state.requestData.password == state.requestData.passwordConfirm &&
        isPasswordValid(state.requestData.password);

    return (
        <>
            <Grid className={classes.container} columns={1}>
                <TitleEmail
                    testIdentifier="VisiFinance.UI.SelfServiceRegister.Email"
                    titleTextCode="VisiFinance.UI.SelfServiceRegister.Email"
                    value={state.requestData.email}
                    setValue={(value) => {
                        dispatch({
                            type: "setRequestData",
                            key: "email",
                            value,
                        });
                    }}
                />
                <FieldError
                    message={
                        state.responseError.email ??
                        (!isEmailValid &&
                            !!state.requestData.email &&
                            intl.formatText(
                                "VisiFinance.UI.SelfServiceRegister.EmailInvalid"
                            ))
                    }
                />
                <TitlePassword
                    testIdentifier="VisiFinance.UI.SelfServiceRegister.Password"
                    titleTextCode="VisiFinance.UI.SelfServiceRegister.Password"
                    value={state.requestData.password}
                    setValue={(value) => {
                        dispatch({
                            type: "setRequestData",
                            key: "password",
                            value,
                        });
                    }}
                    isValid={(val) => isPasswordValid(val)}
                />
                <FieldError message={state.responseError.password} />
                <TitlePassword
                    testIdentifier="VisiFinance.UI.SelfServiceRegister.ConfirmPassword"
                    titleTextCode="VisiFinance.UI.SelfServiceRegister.ConfirmPassword"
                    value={state.requestData.passwordConfirm}
                    setValue={(value) => {
                        dispatch({
                            type: "setRequestData",
                            key: "passwordConfirm",
                            value,
                        });
                    }}
                    isValid={(str) => state.requestData.password == str}
                />
                <FieldError
                    message={
                        !!state.requestData.passwordConfirm &&
                        state.requestData.password !=
                            state.requestData.passwordConfirm &&
                        intl.formatText(
                            "VisiFinance.UI.SelfServiceRegister.ConfirmPasswordMissmatch"
                        )
                    }
                />
            </Grid>
            <SelfServicePageNavigationControls
                state={state}
                dispatch={dispatch}
                postRegistrationRequest={postRegistrationRequest}
                disablePrev
                disableNext={!isFormValid || isLoading}
            />
        </>
    );
};

const isPasswordValid = (password: string) => {
    // TODO: read actual password policy from client settings

    if (!password) {
        return false;
    }

    if (password.length < 8) {
        return false;
    }

    if (password.length > 2048) {
        return false;
    }

    if (/[A-Z]/gm.test(password) == false) {
        return false;
    }

    if (/[a-z]/gm.test(password) == false) {
        return false;
    }
    if (/\d/gm.test(password) == false) {
        return false;
    }
    return true;
};

export default PageAccount;
